import './App.css';
import './components/MultiRangeSlider/MultiRangeSlider.css';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import Signup from './pages/Signup';
import Signin from './pages/Signin';
import Rynek from './pages/Rynek';
import RynekMoje from './pages/RynekMoje';
import RynekOgloszenieFirmy from './pages/RynekOgloszenieFirmy';
import VerifyEmail from './pages/VerifyEmail';
import PasswordResetRequest from './pages/PasswordResetRequest';
import PasswordResetConfirm from './pages/PasswordResetConfirm';
import Account from './pages/Account';
import Preferencje from './pages/Preferencje';
import Panels from './pages/Panels';
import PanelDetails from './pages/PanelDetails';
import ModelDetails from './pages/ModelDetails';
import Inverters from './pages/Inverters';
import DoborFalownika from './pages/DoborFalownika';
import DoborModulu from './pages/DoborModulu';
import DoborMagazynu from './pages/DoborMagazynu';
import DoborZestawuProsty from './pages/DoborZestawuProsty';
import InverterDetails from './pages/InverterDetails';
import Magazines from './pages/Magazines';
import Wallboxes from './pages/Wallboxes';
import WallboxDetails from './pages/WallboxDetails';
import DoborLadowarki from './pages/DoborLadowarki';
import StronaAuta from './pages/StronaAuta';
import MagazineDetails from './pages/MagazineDetails';
import Observed from './pages/Observed';
import Compare from './pages/Compare';
import Compatibility from './pages/Compatibility';
import KompatybilnoscPara from './pages/KompatybilnoscPara';
import KitAssistant from './pages/KitAssistant';
import KnowledgeBase from './pages/KnowledgeBase';
import KnowledgeArticle from './pages/KnowledgeArticle';
import LegalPage from './pages/LegalPage';
import NieZnaleziono from './pages/NieZnaleziono';
import Loader from './components/Loader';
import CompareWidget from './components/CompareWidget';
import ZglosBlad from './components/ZglosBlad';
import UkladZPaskiem, { ObokPaska } from './components/nawigacja/UkladZPaskiem';
import Toast from './components/Toast';
import PomiarOdslon from './components/PomiarOdslon';
import { ThemeProvider } from './ThemeContext';
import MotywMui from './components/MotywMui';
import { SelectionProvider } from './context/SelectionContext';
import { AuthProvider } from './context/AuthContext';
import { SamouczekProvider } from './context/SamouczekContext';
import Samouczek from './components/samouczek/Samouczek';
import PodgladPlanu from './components/plany/PodgladPlanu';
import { OknoPremiumProvider } from './components/plany/OknoPremiumContext';
import BramkaPartnera from './components/auth/BramkaPartnera';
import { PRZYJMUJEMY_ZGLOSZENIA_DYSTRYBUTOROW } from './config/zgloszeniaDystrybutorow';
import { KRAJ } from './kraj';
import i18n from './i18n';

// Moduł wyboru wykonawcy ładowany OSOBNO. Sama mapa to 17 KB ścieżek SVG,
// a razem z katalogiem firm cały moduł jechał w paczce głównej do każdego, kto
// wszedł choćby na listę paneli. Ładuje się dopiero, gdy ktoś na niego wejdzie.
const PotwierdzenieZestawu = lazy(() => import('./pages/PotwierdzenieZestawu'));
const WyborWykonawcy = lazy(() => import('./pages/WyborWykonawcy'));
const ZostanDystrybutorem = lazy(() => import('./pages/ZostanDystrybutorem'));
const AnkietaFirmy = lazy(() => import('./pages/AnkietaFirmy'));
const DlaFirmPV = lazy(() => import('./pages/DlaFirmPV'));
const DlaDomu = lazy(() => import('./pages/DlaDomu'));
const DlaFirmRynek = lazy(() => import('./pages/DlaFirmRynek'));
const Cennik = lazy(() => import('./pages/Cennik'));
const Okazje = lazy(() => import('./pages/Okazje'));
const SprawdzOferte = lazy(() => import('./pages/SprawdzOferte'));
const TeczkaPme2 = lazy(() => import('./pages/TeczkaPme2'));
// Tymczasowa trasa do pokazu części „Miejsca w domu” teczki instalacji (zadanie 2026-10_teczka-instalacji, sesja 5).
const StronaTeczki = lazy(() => import('./components/teczka/StronaTeczki'));
const Klienci = lazy(() => import('./pages/Klienci'));
const Pulpit = lazy(() => import('./pages/Pulpit'));
const DolaczDoFirmy = lazy(() => import('./pages/DolaczDoFirmy'));
const OsobaZespolu = lazy(() => import('./pages/OsobaZespolu'));
const ZgloszenieMikroinstalacji = lazy(() => import('./pages/ZgloszenieMikroinstalacji'));
const ZawiadomieniePsp = lazy(() => import('./pages/ZawiadomieniePsp'));
// Bramka logowania (2026-10-15) wczytuje właściwy Projekt 3D dopiero po zalogowaniu.
const ProjektDachu3D = lazy(() => import('./pages/Projekt3DBramka'));

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const img = new Image();
    img.src = `${process.env.PUBLIC_URL}/navLogo.png`;
    img.onload = () => setLoading(false);
    img.onerror = () => setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <ThemeProvider>
      {/* MUI ma własną paletę i nie widzi klasy `dark` — ten most przekazuje jej
          nasz motyw (paginacja i okno filtrów w `ProductPage.jsx`). */}
      <MotywMui>
      {/* Wersja krajowa ma prefiks w adresie (/ro/...): Link i navigate dokładają go same. */}
      <Router basename={KRAJ.prefiks || undefined}>
        <AuthProvider>
        {/* Okno „Przejdź na Premium” (FE-02): jedno na serwis, otwiera je kłódka i odpowiedź 402 z API. */}
        <OknoPremiumProvider>
        <SelectionProvider>
        {/* Samouczek „Pierwsze kroki” (2026-10-13): potrzebuje sesji i routera, a menu konta otwiera go z paska. */}
        <SamouczekProvider>
          <PomiarOdslon />
          <div className='flex flex-col min-h-screen'>
            <Navbar />
            {/* Pasek narzędzi po lewej (flaga w config/nawigacja.js). Przy
                wyłączonej fladze to dawny `<div className='flex-grow'>`. */}
            <UkladZPaskiem>
              <Suspense fallback={
                <div className="flex justify-center py-24"><Loader label={i18n.t('wspolne:wczytywanie')} /></div>
              }>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/panele" element={<Panels />} />
                <Route path="/panel/:id" element={<PanelDetails />} />
                <Route path="/model/:category/:slugId" element={<ModelDetails />} />
                <Route path="/inwertery" element={<Inverters />} />
                {/* Asystent doboru falownika — OSOBNA strona, nie okno: ma własny
                    adres, da się ją podesłać i wrócić do niej z historii. */}
                <Route path="/dobor-falownika" element={<DoborFalownika />} />
                <Route path="/dobor-modulow" element={<DoborModulu />} />
                <Route path="/dobor-magazynu" element={<DoborMagazynu />} />
                <Route path="/inwerter/:id" element={<InverterDetails />} />
                <Route path="/magazyny" element={<Magazines />} />
          <Route path="/ladowarki" element={<Wallboxes />} />
          {/* Katalog firmowy zwiniety do przelacznika w filtrach - stary adres
              zostaje jako przekierowanie, bo krazyl juz w linkach. */}
          <Route path="/ladowarki-firmowe" element={<Navigate to="/ladowarki" replace />} />
          <Route path="/ladowarka/:id" element={<WallboxDetails />} />
          <Route path="/dobor-ladowarki" element={<DoborLadowarki />} />
          <Route path="/dobor-ladowarki/:strona" element={<StronaAuta />} />
                <Route path="/magazyn/:id" element={<MagazineDetails />} />
                <Route path="/obserwowane" element={<Observed />} />
                <Route path="/porownanie" element={<Compare />} />
                <Route path="/kompatybilnosc" element={<Compatibility />} />
                {/* Strony par z backendu (seo_compat). Bez tej trasy łapała je strona 404 z noindex. */}
                <Route path="/kompatybilnosc/:slug" element={<KompatybilnoscPara />} />
                <Route path="/dobor-zestawu-pv" element={<KitAssistant />} />
                {/* Druga droga do tego samego silnika: pytania o dom i rachunki
                    zamiast o parametry. Osoba pierwszy raz myśląca o fotowoltaice
                    odbija się od pierwszego pola w pełnym asystencie. */}
                <Route path="/dobierz-zestaw" element={<DoborZestawuProsty />} />
                <Route path="/potwierdzenie-zestawu" element={<PotwierdzenieZestawu />} />
                <Route path="/wybor-wykonawcy" element={<WyborWykonawcy />} />
                {/* Rozdroże zostaje otwarte dla wszystkich — ma sprzedać decyzję.
                    Same formularze są za bramką: zgłoszenie przypisujemy do konta,
                    więc niezalogowany widzi rozmyty podgląd i zachętę zamiast pól
                    (components/auth/BramkaPartnera).

                    Bramka TYLKO przy włączonych zapisach. Przy wyłączonej fladze
                    strona nie ma formularza, tylko zapowiedź z adresem kontaktowym —
                    zasłonięcie jej wezwaniem do rejestracji ukryłoby ten adres przed
                    firmą, dla której go tam umieściliśmy. */}
                {/* Jedna droga dla firm (2026-10-22): rozdroże „Zostań partnerem” i zgłoszenie wykonawcy bez konta
                    zastąpiła strona dla firm i konto firmowe z profilem na Rynku. Stare adresy z reklam i artykułów
                    prowadzą na `/dla-firm`. */}
                <Route path="/dodaj-siebie" element={<Navigate to="/dla-firm" replace />} />
                <Route path="/zostan-wykonawca" element={<Navigate to="/dla-firm" replace />} />
                <Route
                  path="/zostan-dystrybutorem"
                  element={PRZYJMUJEMY_ZGLOSZENIA_DYSTRYBUTOROW
                    ? <BramkaPartnera><ZostanDystrybutorem /></BramkaPartnera>
                    : <ZostanDystrybutorem />}
                />
                {/* VoltValue dla firm PV (2026-10-13): strona z filmów marketingowych; dawna lista oczekujących firm zniknęła. */}
                <Route path="/dla-firm" element={<DlaFirmPV />} />
                <Route path="/dla-firm/rynek-ofert" element={<DlaFirmRynek />} />
                {/* Ankieta firmy na Rynku (2026-10-23): bez niej firma nie dostaje zleceń z Rynku. */}
                <Route path="/firma/ankieta" element={<AnkietaFirmy />} />
                {/* Cennik planów Free i Premium (FE-11, 2026-10-19), link z okna Premium, /dla-firm i stopki. */}
                <Route path="/cennik" element={<Cennik />} />
                {/* VoltValue dla domu (2026-10-24, makieta 12, rekomendacja D-a): dwie drogi do zlecenia na Rynku. */}
                <Route path="/dla-domu" element={<DlaDomu />} />
                <Route path="/rynek" element={<Rynek />} />
                <Route path="/rynek/moje" element={<RynekMoje />} />
                <Route path="/rynek/moje/:id" element={<RynekMoje />} />
                <Route path="/rynek/:id" element={<RynekOgloszenieFirmy />} />
                <Route path="/okazje" element={<Okazje />} />
                <Route path="/sprawdz-oferte" element={<SprawdzOferte />} />
                <Route path="/zgloszenie-mikroinstalacji" element={<ZgloszenieMikroinstalacji />} />
                <Route path="/zawiadomienie-psp" element={<ZawiadomieniePsp />} />
                <Route path="/projekt-3d" element={<ProjektDachu3D />} />
                <Route path="/projekt-dachu-3d" element={<Navigate to="/projekt-3d" replace />} />
                <Route path="/teczka-pme2" element={<TeczkaPme2 />} />
                <Route path="/teczka-pme2/:id" element={<TeczkaPme2 />} />
                <Route path="/teczka" element={<StronaTeczki />} />
                {/* Pulpit firmy (2026-10-11): domyślne miejsce po zalogowaniu konta firmowego; prywatne odsyła na stronę główną. */}
                <Route path="/pulpit" element={<Pulpit />} />
                <Route path="/klienci" element={<Klienci />} />
                <Route path="/klienci/:id" element={<Klienci />} />
                <Route path="/baza-wiedzy" element={<KnowledgeBase />} />
                <Route path="/baza-wiedzy/:slug" element={<KnowledgeArticle />} />
                <Route path="/regulamin" element={<LegalPage file="regulamin.md" fallbackTitle="Regulamin" />} />
                <Route path="/polityka-prywatnosci" element={<LegalPage file="polityka-prywatnosci.md" fallbackTitle="Polityka prywatności" />} />
                {/* Konta. Ścieżki /potwierdz-email i /nowe-haslo MUSZĄ zgadzać się
                    z HEADLESS_FRONTEND_URLS w settings.py backendu — to pod nie
                    kierują linki wysyłane w mailach. */}
                <Route path="/rejestracja" element={<Signup />} />
                <Route path="/logowanie" element={<Signin />} />
                <Route path="/potwierdz-email/:key" element={<VerifyEmail />} />
                <Route path="/reset-hasla" element={<PasswordResetRequest />} />
                <Route path="/nowe-haslo/:key" element={<PasswordResetConfirm />} />
                <Route path="/konto" element={<Account />} />
                {/* Link z maila z zaproszeniem do zespołu firmy (FRONTEND_BASE_URL/dolacz/<token> w accounts/zespol.py). */}
                <Route path="/dolacz/:token" element={<DolaczDoFirmy />} />
                {/* Karta osoby z zespołu firmy: zdjęcie, uprawnienia, statystyki i klienci. */}
                <Route path="/zespol/:id" element={<OsobaZespolu />} />
                {/* Preferencje doboru mają WŁASNY adres, a nie zakładkę w `/konto`:
                    to pierwsza pozycja w menu konta i jedyna sekcja, do której
                    wraca się regularnie. */}
                <Route path="/preferencje" element={<Preferencje />} />

                {/* MUSI zostać OSTATNIA — dopasowuje się do wszystkiego, czego
                    nie złapały trasy wyżej. Bez niej nieistniejący adres dawał
                    pustą stronę: nagłówek, stopka i nic pomiędzy. */}
                <Route path="*" element={<NieZnaleziono />} />
              </Routes>
              </Suspense>
            </UkladZPaskiem>
          </div>
          <Footer />
          <CompareWidget />
          <Samouczek />
          {/* Zachęta do zgłaszania błędów — lewy dolny róg, poza prawym, który
              zajmuje porównywarka. Poza `Routes`, bo ma być na KAŻDEJ stronie. */}
          <ObokPaska><ZglosBlad /></ObokPaska>
          <Toast />
          {/* Podgląd planów Free i Premium (FE-01): tylko na localhost po `?plan=…`. */}
          <PodgladPlanu />
        </SamouczekProvider>
        </SelectionProvider>
        </OknoPremiumProvider>
        </AuthProvider>
      </Router>
      </MotywMui>
    </ThemeProvider >
  );
}

export default App;